import React from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';

// SearchableSelect — FORM-03: a long option list (roughly 20 or more: vendors, roads, activities)
// is a select you can type into. Typing filters the options by any part of the label, ignoring
// case; the arrow keys and Enter pick, Escape closes and restores the chosen label. The value is
// controlled exactly like a native <select>: `''` means nothing chosen.

export interface SearchableSelectOption<V extends string | number> {
  value: V;
  label: string;
}

export interface SearchableSelectProps<V extends string | number> {
  id?: string;
  options: SearchableSelectOption<V>[];
  value: V | '';
  onChange: (value: V | '') => void;
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  /** Shown in the list when nothing matches what was typed. */
  noMatchText?: string;
  'aria-label'?: string;
  className?: string;
}

const inputClass =
  'tw-block tw-w-full tw-rounded tw-border tw-bg-surface tw-py-1.5 tw-pl-3 tw-pr-8 tw-text-content tw-transition ' +
  'placeholder:tw-text-content-muted focus:tw-outline-none focus:tw-border-line-focus focus:tw-shadow-focus ' +
  'disabled:tw-bg-surface-alt disabled:tw-opacity-70';

export function SearchableSelect<V extends string | number>({
  id,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  isInvalid = false,
  noMatchText = 'No matches',
  'aria-label': ariaLabel,
  className = '',
}: SearchableSelectProps<V>) {
  const [query, setQuery] = React.useState('');
  const selected = options.find((o) => o.value === value) ?? null;

  const needle = query.trim().toLowerCase();
  const filtered = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  return (
    <Combobox
      value={selected}
      onChange={(option: SearchableSelectOption<V> | null) => {
        onChange(option ? option.value : '');
        setQuery('');
      }}
      onClose={() => setQuery('')}
      disabled={disabled}
      by={(a, b) => a?.value === b?.value}
    >
      <div className={`tw-relative ${className}`}>
        <ComboboxInput
          id={id}
          aria-label={ariaLabel}
          className={`${inputClass} ${isInvalid ? 'tw-border-danger' : 'tw-border-line-input'}`}
          displayValue={(option: SearchableSelectOption<V> | null) => option?.label ?? ''}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
        <ComboboxButton
          className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-content-muted"
          aria-label="Show options"
        >
          <i className="bi bi-chevron-down tw-text-xs" aria-hidden="true" />
        </ComboboxButton>
      </div>
      <ComboboxOptions
        anchor={{ to: 'bottom start', gap: 4, padding: 8 }}
        className="tw-z-[1060] tw-max-h-64 tw-w-[var(--input-width)] tw-overflow-y-auto tw-rounded tw-border tw-border-line tw-bg-surface tw-py-1 tw-shadow-lg empty:tw-invisible focus:tw-outline-none"
      >
        {filtered.length === 0 ? (
          <div className="tw-px-3 tw-py-2 tw-text-content-muted">{noMatchText}</div>
        ) : (
          filtered.map((option) => (
            <ComboboxOption
              key={option.value}
              value={option}
              className="tw-cursor-pointer tw-px-3 tw-py-1.5 tw-text-content data-[focus]:tw-bg-surface-hover data-[selected]:tw-font-semibold"
            >
              {option.label}
            </ComboboxOption>
          ))
        )}
      </ComboboxOptions>
    </Combobox>
  );
}

export default SearchableSelect;
